import React from "react";
import { SymptomsSection } from "../components/SymptomsSection";
import { Link } from "react-router-dom";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { SERVICES, SERVICE_GROUPS } from "../data/services";
import { CaretRight, Info } from "@phosphor-icons/react";

const VASCULAR_COMPARISON = [
  {
    title: "Carotid intima-media thickness (cIMT)",
    slug: "carotid-imt",
    when: "You're in mid-life and your calculated risk score is borderline — you want to know whether more intensive prevention is worthwhile.",
    what: "Measures early thickening of the artery wall in the neck.",
    length: "~15–20 minutes",
  },
  {
    title: "Arterial stiffness measurement",
    slug: "arterial-stiffness",
    when: "You want a measurement of your \"vascular age\" — how stiff or supple your arteries are compared with what's expected for your age.",
    what: "Aortic pulse wave velocity, central blood pressure and augmentation index.",
    length: "~15 minutes",
  },
  {
    title: "Flow-mediated dilatation (FMD)",
    slug: "flow-mediated-dilatation",
    when: "You want a very early marker of vascular health — for example a young patient with a strong family history, or to track how a prevention programme is working.",
    what: "Endothelial function of the brachial artery — one of the earliest changes in cardiovascular disease.",
    length: "~30–45 minutes",
  },
];

function ServiceCard({ service }) {
  return (
    <Link
      to={`/services/${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className="group block p-10 bg-white border border-stone-200/60 rounded-md hover:-translate-y-1 hover:shadow-md transition-all"
    >
      <h3 className="font-serif text-3xl">{service.title}</h3>
      <p className="mt-4 text-ink-muted leading-relaxed">{service.summary}</p>
      <div className="mt-8 text-bronze-dark text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
        Read more <CaretRight size={14} />
      </div>
    </Link>
  );
}

export default function Services() {
  const bySlug = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));
  return (
    <div data-testid="services-page">
      <SEO
        title="Heart, Blood Pressure & Metabolic Health Services in Cambridge"
        description="Consultant physicians in Cambridge for high blood pressure (BP), ECG, echo heart scans, cholesterol, diabetes, weight loss and heart health checks - plus palpitations, dizziness, breathlessness, chest pain and tiredness."
        path="/services"
      />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 max-w-4xl">
          <div className="eyebrow">How we help</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.02]">
            Expert help for <em className="font-serif text-bronze-dark">the things that matter most.</em>
          </h1>
          <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-2xl">
            We focus on what we&apos;re really good at: high blood pressure, cholesterol, and the everyday risk factors for heart attacks and strokes. With access to all of Cambridge University Hospitals&apos; tests and scans behind us.
          </p>
        </div>
      </section>

      <SymptomsSection />

      {SERVICE_GROUPS.map((group) => {
        const services = group.slugs.map((slug) => bySlug[slug]).filter(Boolean);
        if (!services.length) return null;
        return (
          <section
            key={group.key}
            data-testid={`service-group-${group.key}`}
            className="container-ccc py-20 border-b border-stone-200/50 last:border-0"
          >
            <div className="max-w-3xl mb-12">
              <div className="eyebrow">{group.title}</div>
              <p className="mt-3 text-ink-muted text-lg leading-relaxed">{group.subtitle}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((s) => <ServiceCard key={s.slug} service={s} />)}
            </div>

            {group.key === "vascular" && (
              <div
                className="mt-16 p-8 sm:p-10 bg-cream-100 border border-bronze-light/40 rounded-md"
                data-testid="vascular-comparison-panel"
              >
                <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                  <Info size={14} weight="duotone" /> Which vascular-health test is right for me?
                </div>
                <p className="mt-3 text-ink-muted max-w-3xl leading-relaxed">
                  All three look at how healthy your arteries actually are, but they each answer a slightly different question. Any of them is best chosen after a proper consultation — this is just a rough guide.
                </p>
                <div className="mt-8 overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="text-ink border-b border-bronze-light/40">
                        <th className="py-3 pr-4 font-semibold">Test</th>
                        <th className="py-3 pr-4 font-semibold">Best when&hellip;</th>
                        <th className="py-3 pr-4 font-semibold">What it measures</th>
                        <th className="py-3 pr-4 font-semibold">Length</th>
                      </tr>
                    </thead>
                    <tbody className="text-ink-muted align-top">
                      {VASCULAR_COMPARISON.map((row) => (
                        <tr key={row.slug} className="border-b border-stone-200/50 last:border-0">
                          <td className="py-4 pr-4 text-ink font-medium">
                            <Link to={`/services/${row.slug}`} className="hover:text-bronze-dark underline-offset-2 hover:underline">
                              {row.title}
                            </Link>
                          </td>
                          <td className="py-4 pr-4 leading-relaxed">{row.when}</td>
                          <td className="py-4 pr-4 leading-relaxed">{row.what}</td>
                          <td className="py-4 pr-4 whitespace-nowrap">{row.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        );
      })}

      <section className="container-ccc py-24 text-center">
        <SectionHeading
          center
          title="Not sure where you fit?"
          subtitle="Tell us a bit about what&apos;s going on and we&apos;ll point you to the right consultant — there&apos;s no obligation."
        />
        <Link data-testid="services-enquiry-cta" to="/enquiry" className="btn-primary mt-8">
          Get in touch
        </Link>
      </section>
    </div>
  );
}
